/**
 * Resolve caller label for 24/7 Inquiry Call Assist tables and modals.
 *
 * SYNC NOTICE: Keep constants, token lists, and regex patterns in sync with
 *   Albert-backend/src/integrations/agent247/callerNameExtraction.js
 * That file is the canonical source of truth. Do not edit patterns independently.
 *
 * @version 2.0.0
 *
 * Changes in v2.0.0 (mirror of backend fixes):
 *   - FM-1: Removed unsafe bare-fallback in parseUserNameReply (any 1-3 word message
 *     accepted as name). A caller turn now only yields a name via an explicit intro
 *     phrase OR as a bare reply directly after a Kathy name-question (gated).
 *   - FM-2: Extended NAME_QUESTION_RE with "first name" variants for could/can.
 *     Added bare-reply handler inside extractAfterNameQuestion only.
 *   - Token list: added "sorry", "that", "ok", "correct", "right", "um", "uh",
 *     "speaking", and other confirmed false-positive single words.
 */

const toNameCase = (value = "") =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() || ""}${part.slice(1).toLowerCase()}`)
    .join(" ");

const firstNameOnly = (full = "") => {
  const parts = String(full || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  const SKIP_PREFIX_TOKENS = new Set([
    "it", "its", "im", "iam", "this", "is", "yes", "yeah", "yep", "speaking", "me",
  ]);
  const cleaned = parts.filter((part) => {
    const token = String(part || "").toLowerCase().replace(/[^a-z]/g, "");
    return token && !SKIP_PREFIX_TOKENS.has(token);
  });
  if (!cleaned.length) return "";
  return toNameCase(cleaned[0]);
};

// ─── BLOCKLISTS ───────────────────────────────────────────────────────────────
// MUST stay identical to Albert-backend/src/integrations/agent247/callerNameExtraction.js

/**
 * Two-word phrases at the start of a message that rule out a name.
 */
const GARBAGE_TWO = new Set([
  "i have", "i want", "i need", "i am",
  "im looking", "i'm looking", "m looking",
  "yes i", "thank you", "thanks you",
  "hi there", "hello i",
  "that is", "that's", "thats right",
  "yes that", "oh yes", "yes correct",
]);

/**
 * Single-token first-words that can NEVER be a caller's name.
 */
const NEVER_CALLER_TOKENS = new Set([
  // Pronouns
  "i", "it", "its", "me", "you", "we", "they",
  // Affirmatives / negatives
  "yes", "yeah", "yep", "yea",
  "no", "nope", "nah",
  "ok", "okay",
  "sure", "correct", "right", "exactly", "absolutely",
  // Filler / interjections
  "sorry", "that", "thats",
  "hello", "hi", "hey",
  "um", "uh", "hmm", "oh",
  "well", "so", "now",
  // Pleasantries
  "please", "thanks", "thank",
  "great", "fine", "good",
  // Generic role labels
  "kathy", "caller", "user", "customer", "unknown", "agent",
  "sir", "maam",
  "someone", "anyone", "everyone", "nobody",
  // Language names
  "english", "spanish", "french",
  // Confirmed false-positive phrase starters
  "looking", "information", "having", "mistakenly",
  "help", "speaking", "calling", "need",
]);

export const isGarbageCallerLabel = (raw = "") => {
  const t = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:]+$/g, "");
  if (!t || t.length < 2) return true;
  const firstTok = t.split(/\s+/)[0];
  const firstTokPlain = firstTok.replace(/[^a-z]/g, "");
  const plain = t.replace(/[^a-z\s]/g, " ").replace(/\s+/g, " ").trim();
  if (
    NEVER_CALLER_TOKENS.has(firstTok) ||
    NEVER_CALLER_TOKENS.has(t) ||
    NEVER_CALLER_TOKENS.has(firstTokPlain) ||
    NEVER_CALLER_TOKENS.has(plain)
  ) {
    return true;
  }
  const two = t.split(/\s+/).slice(0, 2).join(" ");
  if (GARBAGE_TWO.has(two)) return true;
  if (GARBAGE_TWO.has(t)) return true;
  const badStarts = ["i have ", "i want ", "i need ", "m look", "im look", "looking for", "yes i "];
  if (badStarts.some((p) => t.startsWith(p))) return true;
  return false;
};

// ─── NAME-QUESTION REGEX ──────────────────────────────────────────────────────
// MUST stay identical to Albert-backend/src/integrations/agent247/callerNameExtraction.js
//
// v2: added (first\s+)? to "could/can I get/have your name" variants.
//
// ⚠ PRODUCTION VALIDATION NEEDED: no live transcript samples were available
// during development (dev DB empty). Validate against 3-5 real Unity-Princeton
// call transcripts before considering the regex finalized.
const NAME_QUESTION_RE =
  /\b(what'?s\s+your\s+(first\s+)?name|what\s+is\s+your\s+(first\s+)?name|may\s+i\s+please\s+get\s+your\s+(first\s+)?name|may\s+i\s+(please\s+)?(have\s+your\s+(first\s+)?name|get\s+your\s+(first\s+)?name)|please\s+get\s+your\s+(first\s+)?name|could\s+i\s+(get|have)\s+your\s+(first\s+)?name|can\s+i\s+(get|have)\s+your\s+(first\s+)?name|who\s+am\s+i\s+speaking|who\s+do\s+i\s+have\s+the\s+pleasure|and\s+your\s+name|spell\s+your\s+name)\b/i;

// ─── TURN ROLE HELPERS ────────────────────────────────────────────────────────

const isUserTurn = (role = "") => {
  const r = String(role || "").toLowerCase();
  return r.includes("user") || r.includes("caller") || r.includes("human") || r === "customer";
};

const isAgentTurn = (role = "") => {
  const r = String(role || "").toLowerCase();
  return (
    r.includes("agent") ||
    r.includes("assistant") ||
    r.includes("bot") ||
    r.includes("kathy") ||
    r.includes("system")
  );
};

// ─── NAME PARSING ─────────────────────────────────────────────────────────────

/**
 * Extracts a caller name from a message that contains an explicit intro phrase.
 * FM-1 fix: the unsafe bare-message fallback has been removed. A message that
 * doesn't match an intro phrase returns "".
 */
const parseUserNameReply = (msg) => {
  const m = String(msg || "")
    .trim()
    .replace(/^["'""]+|["'""]+$/g, "");
  if (!m) return "";
  const patterns = [
    /\bmy\s+name\s+is\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
    /\bi'?m\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
    /\bit'?s\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
    /\bthis\s+is\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
    /\b(?:yes|yeah|yep)[,\s:;-]*([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
    /\byou\s+can\s+call\s+me\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
    /\bcall\s+me\s+([A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2})\b/i,
  ];
  for (const re of patterns) {
    const x = m.match(re);
    if (x?.[1]) {
      const cand = toNameCase(x[1].split(/[,;]/)[0].trim());
      if (cand && !isGarbageCallerLabel(cand)) return cand;
    }
  }
  // Bare-fallback intentionally removed in v2 (FM-1 fix).
  // Bare-name acceptance now only happens inside extractAfterNameQuestion
  // when the message is the direct reply to a Kathy name-question.
  return "";
};

/**
 * Accepts a message as a bare name ONLY inside a name-question reply context.
 * Must never be called as a general fallback.
 */
const extractBareNameReply = (msg) => {
  const cleaned = String(msg || "")
    .trim()
    .replace(/^["'""]+|["'""]+$/g, "");
  const compact = cleaned.replace(/[.,!?;:]+/g, " ").replace(/\s+/g, " ").trim();
  if (
    compact.length < 2 ||
    compact.length > 40 ||
    !/^[A-Za-z][A-Za-z'-]*(?:\s+[A-Za-z][A-Za-z'-]*){0,2}$/.test(compact)
  ) {
    return "";
  }
  return toNameCase(compact);
};

/**
 * Scans turn pairs for [Kathy asks name] → [Caller replies].
 * FM-3 fix: also accepts a bare short-name reply IN THIS CONTEXT ONLY.
 */
const extractAfterNameQuestion = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  for (let i = 0; i < list.length - 1; i++) {
    const a = list[i];
    const b = list[i + 1];
    const roleA = String(a?.role || a?.speaker || "").trim();
    const roleB = String(b?.role || b?.speaker || "").trim();
    const msgA = String(a?.message || a?.text || "").trim();
    const msgB = String(b?.message || b?.text || "").trim();
    if (!msgA || !msgB) continue;
    if (!isAgentTurn(roleA) || !isUserTurn(roleB)) continue;
    if (!NAME_QUESTION_RE.test(msgA)) continue;
    // Try intro-phrase first
    const got = parseUserNameReply(msgB);
    if (got) return got;
    // Part 3 fix: bare reply accepted ONLY here (gated to name-question context)
    const bare = extractBareNameReply(msgB);
    if (bare && !isGarbageCallerLabel(bare)) return bare;
  }
  return "";
};

// ─── PAYLOAD READING HELPERS ──────────────────────────────────────────────────

const readAny = (obj, keys) => {
  if (!obj) return "";
  for (const key of keys) {
    const parts = String(key).split(".");
    let cur = obj;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else {
        cur = undefined;
        break;
      }
    }
    const v = cur;
    if (v !== undefined && v !== null && String(v).trim()) return String(v).trim();
  }
  return "";
};

const agentFirstNameLower = (payload) => {
  const raw = readAny(payload, [
    "normalized.agentName",
    "agentName",
    "agent_name",
    "data.agent_name",
    "data.agent.name",
  ]);
  const first = String(raw || "").trim().split(/\s+/)[0].toLowerCase();
  return first || "";
};

const callerMatchesAgentFirstName = (candidate, payload) => {
  const agent = agentFirstNameLower(payload);
  if (!agent || !candidate) return false;
  return firstNameOnly(candidate).toLowerCase() === agent;
};

const isOkCallerCandidate = (name, payload) =>
  Boolean(name) && !isGarbageCallerLabel(name) && !callerMatchesAgentFirstName(name, payload);

const deriveFromSummary = (payload) => {
  const summaryText = String(
    payload?.summary ||
      payload?.normalized?.summary ||
      payload?.data?.analysis?.transcript_summary ||
      "",
  );
  const patterns = [
    /\b(?:the\s+user|caller)\s*,?\s*([A-Za-z][A-Za-z'-]{1,30}(?:\s+[A-Za-z][A-Za-z'-]{1,30}){0,2})\s*,?\s+(?:contacted|called|requested|asked)\b/i,
    /\bidentified as\s+([A-Za-z]+(?:[\s-][A-Za-z]+){0,2})\b/i,
    /\bgathered\s+([A-Za-z][A-Za-z'-]{1,30}(?:\s+[A-Za-z][A-Za-z'-]{1,30}){0,2})'?s\s+name\b/i,
  ];
  for (const re of patterns) {
    const m = summaryText.match(re);
    if (!m?.[1]) continue;
    const cand = toNameCase(m[1]);
    if (isOkCallerCandidate(cand, payload)) return cand;
  }
  return "";
};

const deriveFromTranscriptIntros = (payload) => {
  const transcriptRows = Array.isArray(payload?.data?.transcript)
    ? payload.data.transcript
    : Array.isArray(payload?.transcript)
      ? payload.transcript
      : [];
  for (const row of transcriptRows) {
    if (!isUserTurn(String(row?.role || row?.speaker || ""))) continue;
    const msg = String(row?.message || row?.text || "").trim();
    const intro = msg.match(
      /\b(?:this is|i am|i'm|yes)[,\s:;-]*([A-Za-z]+(?:[\s-][A-Za-z]+){0,2})\b/i,
    );
    if (intro?.[1]) {
      const cand = toNameCase(intro[1]);
      if (isOkCallerCandidate(cand, payload)) return cand;
    }
  }
  return "";
};

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────

/**
 * @param {object} payload — event.payload from API (may include nested data / normalized)
 * @returns {string} First name for display (fallback: "Caller")
 */
export const resolveCallerFirstName = (payload = {}) => {
  if (!payload || typeof payload !== "object") return "Caller";

  // Perf-audit reorder (2026-07-24): `normalized.callerName` / `callerName` is
  // buildCallerName()'s output — computed server-side at ingest time by the canonical
  // backend module (callerNameExtraction.js), which already cross-validates the
  // ElevenLabs analysis name against the transcript before accepting it (see that
  // file's FM-3 fix). It is NOT the same as the raw, unvalidated `data.analysis.caller_name`
  // LLM guess checked further below — it's already the trustworthy, transcript-corroborated
  // answer. Checking it first (instead of last) means the common case never needs
  // `transcriptTurns` at all, which is what let the list endpoint stop shipping the full
  // per-event transcript array to the browser. Events from ingestion paths that never
  // populated this field (older events, or the non-agent247 webhook path) fall through
  // to the original transcript-walk/analysis/summary heuristics below, unchanged.
  const stored = readAny(payload, ["normalized.callerName", "callerName"]) || "";
  if (stored && isOkCallerCandidate(toNameCase(stored), payload)) {
    return firstNameOnly(toNameCase(stored));
  }

  const transcriptRows = Array.isArray(payload?.transcriptTurns)
    ? payload.transcriptTurns
    : Array.isArray(payload?.transcript)
      ? payload.transcript
      : Array.isArray(payload?.normalized?.transcriptTurns)
        ? payload.normalized.transcriptTurns
        : Array.isArray(payload?.data?.transcript)
          ? payload.data.transcript
          : [];

  const normalizedRows = transcriptRows.map((row) => ({
    role: String(row?.role || row?.speaker || "").trim(),
    message: String(row?.message || row?.text || "").trim(),
  }));

  const afterAsk = extractAfterNameQuestion(normalizedRows);
  if (afterAsk && isOkCallerCandidate(afterAsk, payload)) {
    return firstNameOnly(afterAsk);
  }

  const dynamicName = readAny(payload, [
    "conversation_initiation_client_data.dynamic_variables.candidate_name",
    "conversation.conversation_initiation_client_data.dynamic_variables.candidate_name",
    "metadata.dynamic_variables.candidate_name",
  ]);
  if (dynamicName && isOkCallerCandidate(toNameCase(dynamicName), payload)) {
    return firstNameOnly(toNameCase(dynamicName));
  }

  const analysisName = readAny(payload, [
    "data.analysis.caller_name",
    "data.analysis.user_name",
    "data.analysis.customer_name",
  ]);
  if (analysisName && isOkCallerCandidate(toNameCase(analysisName), payload)) {
    return firstNameOnly(toNameCase(analysisName));
  }

  const fromSummary = deriveFromSummary(payload);
  if (fromSummary) return firstNameOnly(fromSummary);

  const fromIntro = deriveFromTranscriptIntros(payload);
  if (fromIntro) return firstNameOnly(fromIntro);

  return "Caller";
};
